/** The app's pages, each a file of its own that is fetched when the page is first needed: a visitor to one tool
 *  does not download the other five, and nobody but the administrator downloads the admin. The landing page and
 *  the frame every page shares stay in the first file.
 *
 *  The server names the file of the page asked for in the HTML (`<link rel="modulepreload">`, api/seo.py reading
 *  the build's manifest), so the browser fetches it alongside the first file rather than after it. Moving the
 *  pointer over a menu item fetches that page ahead of the click (`preload`). */
import { createElement, lazy, useState, type ComponentType } from 'react'

const RELOADED = 'page-file-reloaded'

/** After a deploy, a page a visitor still has open names files that are gone: the new page is loaded, once. */
function fetched<T>(what: () => Promise<T>): Promise<T> {
  return what().then((m) => {
    try { sessionStorage.removeItem(RELOADED) } catch { /* storage is off: nothing to forget */ }
    return m
  }).catch((e) => {
    let again = false
    try { again = !sessionStorage.getItem(RELOADED); if (again) sessionStorage.setItem(RELOADED, '1') } catch { /* no storage, no retry */ }
    if (!again) throw e
    window.location.reload()
    return new Promise<T>(() => {})          // (the page is going away)
  })
}

/** A page component that is its file's own component once the file has loaded. Through `lazy` alone, every first
 *  render of a page suspended, even with the file in hand, and React holds back what a suspended tree shows (and
 *  its effects, so its requests) for about 300 ms: the Calculator asked for its prices 0.4 s after the click
 *  (2026-10-15). An instance keeps the way it started, so a page is never mounted twice. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function page<C extends ComponentType<any>>(importer: () => Promise<C>) {
  let ready: C | null = null
  let pending: Promise<C> | null = null
  const load = () => (pending ??= fetched(importer).then((c) => (ready = c)).catch((e) => { pending = null; throw e }))
  const Lazy = lazy(() => load().then((c) => ({ default: c })))
  function Page(props: object) {
    const [direct] = useState(() => ready)
    return createElement((direct ?? Lazy) as ComponentType<object>, props)
  }
  return Object.assign(Page as unknown as C, { preload: () => { void load().catch(() => {}) } })
}

export const Calculator = page(() => import('./components/Calculator').then((m) => m.Calculator))
export const ComparePage = page(() => import('./components/ComparePage').then((m) => m.ComparePage))
export const FitPage = page(() => import('./components/FitPage').then((m) => m.FitPage))
export const ChatPanel = page(() => import('./components/ChatPanel').then((m) => m.ChatPanel))
export const NewsPage = page(() => import('./components/NewsPage').then((m) => m.NewsPage))
export const DataPage = page(() => import('./components/DataPage').then((m) => m.DataPage))
export const ExplorePage = page(() => import('./components/Explore').then((m) => m.ExplorePage))
export const LegalPage = page(() => import('./components/LegalPage').then((m) => m.LegalPage))
export const AboutPage = page(() => import('./components/CompanyPage').then((m) => m.AboutPage))
export const ContactPage = page(() => import('./components/CompanyPage').then((m) => m.ContactPage))
export const NeutralityPage = page(() => import('./components/CompanyPage').then((m) => m.NeutralityPage))
export const SimulationsPage = page(() => import('./components/Simulations').then((m) => m.SimulationsPage))
export const SimulationPage = page(() => import('./components/Simulations').then((m) => m.SimulationPage))
export const OptimisePage = page(() => import('./components/OptimisePage').then((m) => m.OptimisePage))
export const SimulatePage = page(() => import('./components/SimulatePage').then((m) => m.SimulatePage))
export const AssessmentPage = page(() => import('./components/AssessmentPage').then((m) => m.AssessmentPage))
export const AdminPage = page(() => import('./components/AdminPage').then((m) => m.AdminPage))
/** The guided demo with its steps: fetched when Demo is pressed. */
export const GuidedTour = page(() => import('./components/GuidedTour').then((m) => m.GuidedTour))
